import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton, StatusBadge } from "@/components/common/Common";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, Plus, MoreHorizontal, Pencil, Trash2, Download, Search, PackagePlus, AlertTriangle } from "lucide-react";

const fmt = (n) => `TZS ${Number(n || 0).toLocaleString()}`;
const empty = { name: "", sku: "", category: "Accessories", brand: "", price: 0, cost: 0, stock_qty: 0, reorder_level: 8, branch_id: "", status: "active" };

export default function ProductsPage() {
  const { t, can, user } = useApp();
  const [products, setProducts] = useState(null);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [delta, setDelta] = useState(0);

  const load = async () => {
    try {
      const [p, b] = await Promise.all([api.get("/products"), api.get("/branches").catch(() => ({ data: [] }))]);
      setProducts(p.data); setBranches(b.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...empty, branch_id: branches[0]?.id || "" }); setDialog(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setDialog(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { branch_id, ...payload } = form;
        await api.put(`/products/${editing.id}`, payload); toast.success("Product updated");
      } else { await api.post("/products", form); toast.success("Product added"); }
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doDelete = async (p) => {
    try { await api.delete(`/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const doAdjust = async () => {
    try { await api.patch(`/products/${adjust.id}/stock`, { delta: Number(delta), reason: "manual" }); toast.success("Stock updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setAdjust(null); setDelta(0);
  };
  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/products/export`, "_blank");

  const filtered = (products || []).filter((p) => !search || `${p.name} ${p.sku} ${p.brand} ${p.category}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("products")} subtitle={t("inventory")}>
        {can("reports.export") && <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv} data-testid="products-export-btn"><Download className="h-4 w-4" />{t("export")}</Button>}
        {can("products.create") && <Button size="sm" className="gap-2" onClick={openCreate} data-testid="create-product-btn"><Plus className="h-4 w-4" />{t("createProduct")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="products-search" />
          </div>
        </div>
        {products === null ? <TableSkeleton cols={6} /> : filtered.length === 0 ? (
          <EmptyState icon={Package} title={t("noProducts")}
            action={can("products.create") && <Button size="sm" className="gap-2" onClick={openCreate}><Plus className="h-4 w-4" />{t("createProduct")}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("sku")}</TableHead><TableHead>{t("name")}</TableHead><TableHead>{t("category")}</TableHead>
                <TableHead>{t("price")}</TableHead><TableHead>{t("stock")}</TableHead><TableHead>{t("branch")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.sku}`}>
                    <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                    <TableCell className="font-medium">{p.name}<span className="block text-xs text-muted-foreground">{p.brand}</span></TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{p.category}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap">{fmt(p.price)}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        {p.stock_qty}
                        {p.low_stock && <span className="inline-flex items-center gap-1 text-[hsl(var(--warning))] text-xs"><AlertTriangle className="h-3 w-3" />{t("lowStock")}</span>}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.branch_name}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`product-actions-${p.sku}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can("products.edit") && <DropdownMenuItem onClick={() => openEdit(p)} data-testid={`product-edit-${p.sku}`}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>}
                          {can("inventory.adjust") && <DropdownMenuItem onClick={() => { setAdjust(p); setDelta(0); }} data-testid={`product-adjust-${p.sku}`}><PackagePlus className="h-4 w-4 mr-2" />{t("adjustStock")}</DropdownMenuItem>}
                          {can("products.delete") && <DropdownMenuItem className="text-destructive" onClick={() => setConfirm(p)} data-testid={`product-delete-${p.sku}`}><Trash2 className="h-4 w-4 mr-2" />{t("delete")}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("editProduct") : t("createProduct")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>{t("name")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="product-name-input" /></div>
            <div><Label>{t("sku")} *</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1.5" data-testid="product-sku-input" /></div>
            <div><Label>{t("category")}</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" data-testid="product-category-input" /></div>
            <div><Label>{t("brand")}</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5" data-testid="product-brand-input" /></div>
            {!editing && (
              <div><Label>{t("branch")} *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="product-branch-select"><SelectValue placeholder={t("selectBranch")} /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>{t("price")} (TZS) *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="mt-1.5" data-testid="product-price-input" /></div>
            <div><Label>{t("cost")} (TZS)</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} className="mt-1.5" data-testid="product-cost-input" /></div>
            {!editing && <div><Label>{t("stock")}</Label><Input type="number" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: Number(e.target.value) })} className="mt-1.5" data-testid="product-stock-input" /></div>}
            <div><Label>{t("reorderLevel")}</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: Number(e.target.value) })} className="mt-1.5" data-testid="product-reorder-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.sku || (!editing && !form.branch_id)} data-testid="product-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjust} onOpenChange={() => setAdjust(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("adjustStock")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{adjust?.name} — current stock: <span className="font-semibold">{adjust?.stock_qty}</span></p>
          <div><Label>Change (+/-)</Label><Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} className="mt-1.5" data-testid="adjust-delta-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjust(null)}>{t("cancel")}</Button>
            <Button onClick={doAdjust} data-testid="adjust-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} destructive
        title="Delete product?" description={`Delete "${confirm?.name}"? This cannot be undone.`} confirmLabel={t("delete")} onConfirm={() => doDelete(confirm)} />
    </div>
  );
}
